import { useEffect, useRef, useState } from 'react';
import mermaid from 'mermaid';
import { Copy, Check, Maximize2 } from 'lucide-react';
import type { Artifact } from '@/types';

// Initialize mermaid with dark theme
mermaid.initialize({
  startOnLoad: false,
  theme: 'dark',
  securityLevel: 'loose',
  fontFamily: 'Inter, system-ui, sans-serif',
});

interface MermaidDiagramProps {
  artifact: Artifact;
}

export function MermaidDiagram({ artifact }: MermaidDiagramProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const renderDiagram = async () => {
      if (!containerRef.current) return;

      try {
        // Clear previous content
        containerRef.current.innerHTML = '';

        // Generate unique ID for this diagram
        const id = `mermaid-${artifact.id}`;

        // Render the diagram
        const { svg } = await mermaid.render(id, artifact.content);
        containerRef.current.innerHTML = svg;
        setError(null);
      } catch (err) {
        console.error('Mermaid rendering error:', err);
        setError(err instanceof Error ? err.message : 'Failed to render diagram');
      }
    };

    renderDiagram();
  }, [artifact.content, artifact.id]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(artifact.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div className="rounded-lg border border-gray-700 bg-gray-800">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-gray-700 px-4 py-2">
        <span className="text-sm font-medium text-gray-100">
          {artifact.title ?? 'Diagram'}
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={copyToClipboard}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-400 hover:bg-gray-700"
            title="Copy Mermaid code"
          >
            {copied ? (
              <>
                <Check className="h-3 w-3 text-green-400" />
                Copied
              </>
            ) : (
              <>
                <Copy className="h-3 w-3" />
                Copy
              </>
            )}
          </button>
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 rounded px-2 py-1 text-xs text-gray-400 hover:bg-gray-700"
            title={expanded ? 'Collapse' : 'Expand'}
          >
            <Maximize2 className="h-3 w-3" />
          </button>
        </div>
      </div>

      {/* Diagram */}
      <div
        className={`overflow-auto p-4 ${expanded ? 'max-h-none' : 'max-h-96'}`}
      >
        {error ? (
          <div className="rounded-lg bg-red-900/20 p-4 text-sm text-red-400">
            <div className="font-medium">Failed to render diagram</div>
            <pre className="mt-2 whitespace-pre-wrap text-xs">{error}</pre>
          </div>
        ) : (
          <div
            ref={containerRef}
            className="mermaid flex items-center justify-center"
          />
        )}
      </div>
    </div>
  );
}
